import React, { useMemo, useState } from 'react';
import { AutoSizer, Table, Column } from 'react-virtualized';
import { IoMdArrowDroprightCircle, IoMdArrowDropdownCircle } from "react-icons/io";

const PivotTableByWeek = ({ filteredData }) => {
    const { flattenedRows, allWeekDates } = useMemo(
        () => transformDataByWeek(filteredData),
        [filteredData]
    );

    const colStats = useMemo(
        () => getColumnMinMax(flattenedRows, allWeekDates),
        [flattenedRows, allWeekDates]
    );
    
    const [expandedDepartments, setExpandedDepartments] = useState({});

    const toggleDepartment = (deptName) => {
        setExpandedDepartments((prev) => ({
            ...prev,
            [deptName]: !prev[deptName],
        }));
    };

    function getColumnMinMax(flattenedRows, allWeekDates) {
        const stats = {};

        for (const dateKey of allWeekDates) {
            let valuesForCol = flattenedRows.map(r => r.dateValues[dateKey] || 0);

            const minVal = Math.min(...valuesForCol);
            const maxVal = Math.max(...valuesForCol);

            stats[dateKey] = { minVal, maxVal };
        }

        return stats; 
    }

    function getColorScale(value, minVal, maxVal) {
        if (minVal === maxVal) {
            return 'rgb(45, 191, 18)';
        }

        const ratio = (value - minVal) / (maxVal - minVal);

        // Interpolate between green (#2DBF12) and red (#FF0000)
        const minR = 45,  minG = 191, minB = 18;
        const maxR = 255, maxG = 0,   maxB = 0;
        const r = Math.round(minR + (maxR - minR) * ratio);
        const g = Math.round(minG + (maxG - minG) * ratio);
        const b = Math.round(minB + (maxB - minB) * ratio);

        return `rgb(${r}, ${g}, ${b})`;
    }

    const finalRows = [];
    for (const row of flattenedRows) {
        if (row.type === 'department') {
            finalRows.push(row);
        } else if (row.type === 'site') {
            const parentDept = row.department;
            if (expandedDepartments[parentDept]) {
                finalRows.push(row);
            }
        }
    }

    const rowGetter = ({ index }) => finalRows[index];

    function transformDataByWeek(allRows) {
        const uniqueWeeksSet = new Set();
        for (const row of allRows) {
            if (row.weekending) {
                const dateKey = row.weekending.split('T')[0]
                uniqueWeeksSet.add(dateKey);
            }
        }
        const allWeekDates = [...uniqueWeeksSet].sort();

        const byDeptThenSite = {};

        for (const row of allRows) {
            const department = row.department || 'Unknown';
            const dateKey = row.weekending.split('T')[0] || 'NoDate';
            const site = row.site || 'NoSite';

            if (!byDeptThenSite[department]) {
                byDeptThenSite[department] = {
                    department,
                    type: 'department',
                    dateValues: {}, 
                    sites: {},     
                };
             
                for (const d of allWeekDates) {
                    byDeptThenSite[department].dateValues[d] = 0;
                }
            }

            byDeptThenSite[department].dateValues[dateKey] =
                (byDeptThenSite[department].dateValues[dateKey] || 0) + (row.nettCost || 0);

            if (!byDeptThenSite[department].sites[site]) {
                byDeptThenSite[department].sites[site] = {
                    department,
                    site,
                    type: 'site',
                    dateValues: {},
                };
             
                for (const d of allWeekDates) {
                    byDeptThenSite[department].sites[site].dateValues[d] = 0;
                }
            }

            byDeptThenSite[department].sites[site].dateValues[dateKey] += (row.nettCost || 0);
        }
        
        const flattenedRows = [];
        for (const deptKey of Object.keys(byDeptThenSite)) {
            const deptObj = byDeptThenSite[deptKey];
            flattenedRows.push(deptObj);
            
            for (const siteKey of Object.keys(deptObj.sites)) {
                flattenedRows.push(deptObj.sites[siteKey]);
            }
        }

        return { flattenedRows, allWeekDates };
    }

    const rowHighlightStyle = (index) => {
        if (filteredData.length > 0 && index >= 0) {
            const rowData = filteredData[index];
            if (rowData.rowHighlightColour) {
                return { backgroundColor: rowData.rowHighlightColour };
            } else if (rowData.resolutionContent?.includes("Closed")) {
                return { backgroundColor: '#ddd', textDecoration: 'line-through' };
            } else {
                return { backgroundColor: 'white' };
            }
        }
    }

    const widthOfDiv = getWidthOfClass('summary-report-container');

    function getWidthOfClass(className) {
        const elements = document.getElementsByClassName(className);
        if (elements.length === 0) return 0;
        const width = elements[0].getBoundingClientRect().width;
        return width > 1920 ? width : 4220;
    }
    
    return (
        <AutoSizer>
            {({height}) => (
                <Table
                    width={widthOfDiv}
                    height={height}
                    rowHeight={60}
                    rowRender={50}
                    rowCount={finalRows.length}
                    rowGetter={rowGetter}
                    rowStyle={({index}) => rowHighlightStyle(index)}
                    horizontal={true}
                    scrollingResetTimeInterval={0}
                    rowClassName={"mangerTableRow"}
                    layout="horizontal"
                >

                    <Column
                        label="Department / Site"
                        dataKey="department"
                        width={250} minWidth={250} flexGrow={1}
                        cellRenderer={({ rowData }) => {
                            if (rowData.type === 'department') {
                                const isExpanded = expandedDepartments[rowData.department] || false;
                                return (
                                    <div style={{ cursor: 'pointer' }} onClick={() => toggleDepartment(rowData.department)}>
                                        {isExpanded ? <IoMdArrowDropdownCircle /> : <IoMdArrowDroprightCircle />} {rowData.department}
                                    </div>
                                );
                            } else if (rowData.type === 'site') {
                                return (
                                    <div style={{ marginLeft: 20, whiteSpace: 'nowrap', textWrap: 'wrap' }}>
                                        {rowData.site}
                                    </div>
                                );
                            }
                            return null;
                        }}
                    />

                    {allWeekDates.map((dateKey) => (
                        <Column
                            key={dateKey}
                            label={dateKey}
                            dataKey={dateKey}
                            width={120} flexGrow={1}
                            headerStyle={{
                                textAlign: 'center'
                            }}
                            style={{padding: 'unset'}}
                            cellRenderer={({ rowData, style }) => {
                                const value = rowData.dateValues[dateKey] || 0;
                                const { minVal, maxVal } = colStats[dateKey];
                                const backgroundColor = value !== 0 ? getColorScale(value, minVal, maxVal) : '';
                                const colour = value !== 0 ? '#ffffff' : 'black';

                                const cellStyle = {
                                    backgroundColor,
                                    width: '100%',
                                    height: '100%',
                                    boxSizing: 'border-box',
                                    color: colour,
                                };
                                
                                return (
                                    <div className="d-flex align-items-center justify-content-center" style={cellStyle}>
                                        £{value.toFixed(2)}
                                    </div>
                                );
                            }}
                        />
                    ))}
                </Table>
            )}
        </AutoSizer>
    );
}

export default PivotTableByWeek;
